import { RefObject, useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

interface DialogA11yOptions {
  /** Whether the dialog is currently open/mounted. */
  isOpen: boolean;
  /** Called when the user requests dismissal (Escape). */
  onClose: () => void;
  /** Ref to the dialog container element (the focus-trap boundary). */
  containerRef: RefObject<HTMLElement>;
  /** Close when Escape is pressed. Default true. */
  closeOnEscape?: boolean;
  /** Lock body scroll while open. Default true. */
  lockScroll?: boolean;
}

/**
 * Adds the standard accessible-dialog behaviors to an existing modal/overlay
 * without dictating its markup:
 *  - moves focus into the dialog on open (first focusable, else the container)
 *  - traps Tab / Shift+Tab focus within the dialog
 *  - closes on Escape
 *  - restores focus to the previously-focused element on close
 *  - locks body scroll while open
 *
 * The consumer is still responsible for rendering `role="dialog"`,
 * `aria-modal="true"` and an `aria-labelledby` pointing at the title.
 */
export function useDialogA11y({
  isOpen,
  onClose,
  containerRef,
  closeOnEscape = true,
  lockScroll = true,
}: DialogA11yOptions): void {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Keep the latest onClose without re-running the effect on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const container = containerRef.current;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Move focus into the dialog.
    if (container) {
      const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        container.setAttribute("tabindex", "-1");
        container.focus();
      }
    }

    // Lock body scroll.
    const previousOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = "hidden";

    const handleKeyDown = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !container) return;

      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null); // visible only
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      if (lockScroll) document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen, containerRef, closeOnEscape, lockScroll]);
}
